import React, { useEffect, useState } from 'react';
import { worksSectionApi } from '../../../services/api';

type WorksForm = {
  badge_text: string;
  heading: string;
  description: string;
  stat1_label: string;
  stat1_value: string;
  stat2_label: string;
  stat2_value: string;
  stat3_label: string;
  stat3_value: string;
};

const defaultForm: WorksForm = {
  badge_text: '3D Analysis',
  heading: 'Emprise Work Accuracy',
  description:
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.',
  stat1_label: 'Accuracy',
  stat1_value: '99.8%',
  stat2_label: 'Work',
  stat2_value: '60',
  stat3_label: 'Awards',
  stat3_value: '10'
};

const WorksSectionEditor: React.FC = () => {
  const [form, setForm] = useState<WorksForm>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await worksSectionApi.get();
        if (res?.success && res?.data) {
          setForm({
            badge_text: String(res.data.badge_text ?? defaultForm.badge_text),
            heading: String(res.data.heading ?? defaultForm.heading),
            description: String(res.data.description ?? defaultForm.description),
            stat1_label: String(res.data.stat1_label ?? defaultForm.stat1_label),
            stat1_value: String(res.data.stat1_value ?? defaultForm.stat1_value),
            stat2_label: String(res.data.stat2_label ?? defaultForm.stat2_label),
            stat2_value: String(res.data.stat2_value ?? defaultForm.stat2_value),
            stat3_label: String(res.data.stat3_label ?? defaultForm.stat3_label),
            stat3_value: String(res.data.stat3_value ?? defaultForm.stat3_value)
          });
        }
      } catch {
        setMessage('Failed to load works section settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setField = (key: keyof WorksForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setMessage('');
      const res = await worksSectionApi.update(form);
      if (res?.success) {
        setMessage('Works section updated successfully.');
        window.dispatchEvent(new CustomEvent('works:updated'));
      } else {
        setMessage(res?.message || 'Failed to save works section.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save works section.';
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-white/70">Loading works section...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Works Section</h2>
        <p className="text-gray-400">Manage front-page works content and stats.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-2">Badge Text</label>
          <input value={form.badge_text} onChange={(e) => setField('badge_text', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-2">Heading</label>
          <input value={form.heading} onChange={(e) => setField('heading', e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-300 mb-2">Description</label>
        <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={5} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-300 font-semibold">Stat 1</p>
          <input value={form.stat1_label} onChange={(e) => setField('stat1_label', e.target.value)} placeholder="Label" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
          <input value={form.stat1_value} onChange={(e) => setField('stat1_value', e.target.value)} placeholder="Value" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-300 font-semibold">Stat 2</p>
          <input value={form.stat2_label} onChange={(e) => setField('stat2_label', e.target.value)} placeholder="Label" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
          <input value={form.stat2_value} onChange={(e) => setField('stat2_value', e.target.value)} placeholder="Value" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <p className="text-sm text-gray-300 font-semibold">Stat 3</p>
          <input value={form.stat3_label} onChange={(e) => setField('stat3_label', e.target.value)} placeholder="Label" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
          <input value={form.stat3_value} onChange={(e) => setField('stat3_value', e.target.value)} placeholder="Value" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
      </div>

      {message && (
        <div className="text-sm text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3">
          {message}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold">
          {saving ? 'Saving...' : 'Save Works Section'}
        </button>
      </div>
    </div>
  );
};

export default WorksSectionEditor;
